import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, Pause, CalendarCheck2, Scissors, UtensilsCrossed, Pizza, BadgeCheck } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal } from "./Reveal";
import { LogoMark } from "./Logo";

const STR = {
  it: {
    sub: "Tre chiamate vere, registrate: così risponde l'AI quando un cliente chiama davvero.",
    tabs: ["Parrucchiere", "Ristorante · Tavoli", "Ristorante · Pizze"],
    note: "Chiamata reale · Italiano", caller: "Cliente", confirm: "Confermato", live: "AI IN LINEA",
  },
  de: {
    sub: "Drei echte, aufgezeichnete Anrufe: So antwortet die KI, wenn ein Kunde wirklich anruft.",
    tabs: ["Coiffeur", "Restaurant · Tische", "Restaurant · Pizza"],
    note: "Echter Anruf · Italienisch", caller: "Kunde", confirm: "Bestätigt", live: "KI ONLINE",
  },
  fr: {
    sub: "Trois vrais appels enregistrés : ainsi l'IA répond quand un client appelle vraiment.",
    tabs: ["Coiffeur", "Restaurant · Tables", "Restaurant · Pizzas"],
    note: "Vrai appel · Italien", caller: "Client", confirm: "Confirmé", live: "IA EN LIGNE",
  },
  en: {
    sub: "Three real recorded calls: this is how the AI answers when a customer actually calls.",
    tabs: ["Hair salon", "Restaurant · Tables", "Restaurant · Pizza"],
    note: "Real call · Italian", caller: "Customer", confirm: "Confirmed", live: "AI ONLINE",
  },
};

const CALLS = [
  { id: "parrucchiere", icon: Scissors, dur: "1:34" },
  { id: "ristorante", icon: UtensilsCrossed, dur: "1:17" },
  { id: "pizze", icon: Pizza, dur: "2:03" },
];

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const ding = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const tone = (f, st, d) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      g.gain.setValueAtTime(0, st);
      g.gain.linearRampToValueAtTime(0.09, st + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, st + d);
      o.connect(g).connect(ctx.destination);
      o.start(st);
      o.stop(st + d + 0.05);
    };
    const t0 = ctx.currentTime + 0.05;
    tone(987.77, t0, 0.22);
    tone(1318.5, t0 + 0.11, 0.42);
  } catch {}
};

export const AiDemo = () => {
  const { t, lang } = useLang();
  const s = STR[lang] || STR.it;
  const [active, setActive] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [now, setNow] = useState(0);
  const [ended, setEnded] = useState(false);
  const [data, setData] = useState(null);
  const dataRef = useRef({});
  const audioRef = useRef(null);
  const scrollRef = useRef(null);

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  useEffect(() => () => stop(), []);

  const visibleCount = data ? data.lines.filter((l) => l.start <= now + 0.15).length : 0;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [visibleCount, ended]);

  const toggle = async (id) => {
    if (active === id && audioRef.current) {
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
      } else {
        audioRef.current.play().catch(() => {});
        setPlaying(true);
      }
      return;
    }
    stop();
    setEnded(false);
    setNow(0);
    if (!dataRef.current[id]) {
      const r = await fetch(`/audio/calls/${id}.json`);
      dataRef.current[id] = await r.json();
    }
    setData(dataRef.current[id]);
    setActive(id);
    const a = new Audio(`/audio/calls/${id}.mp3`);
    a.ontimeupdate = () => setNow(a.currentTime);
    a.onended = () => {
      setPlaying(false);
      setEnded(true);
      ding();
    };
    audioRef.current = a;
    a.play().catch(() => {});
    setPlaying(true);
  };

  const activeData = active ? dataRef.current[active] : null;

  return (
    <section id="ai-demo" data-testid="ai-demo-widget" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute right-0 top-24 h-72 w-72 rounded-full bg-cyan-500/8 blur-[100px]" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <Reveal className="max-w-3xl">
          <p className="font-mono2 text-xs uppercase tracking-[0.28em] text-cyan-400">{t.demo.kicker}</p>
          <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
            {t.demo.title}
          </h2>
          <p className="mt-4 text-base text-slate-400 sm:text-lg">{s.sub}</p>
        </Reveal>

        <div className="mt-14 grid items-stretch gap-6 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div className="flex h-full flex-col gap-3">
              {CALLS.map((c, i) => {
                const Icon = c.icon;
                const isActive = active === c.id;
                const d = isActive && activeData ? activeData.duration : null;
                return (
                  <button
                    key={c.id}
                    onClick={() => toggle(c.id)}
                    data-testid={`demo-play-${c.id}`}
                    className={`glass group relative flex-1 overflow-hidden rounded-2xl p-5 text-left transition-all duration-300 hover:-translate-y-0.5 ${
                      isActive ? "border-emerald-500/50 shadow-[0_0_40px_rgba(16,185,129,0.15)]" : "hover:border-slate-600"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <span
                        className={`relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full transition-all ${
                          isActive
                            ? "bg-gradient-to-br from-emerald-500 to-cyan-500 text-slate-950"
                            : "border border-slate-700 bg-slate-900/70 text-emerald-400 group-hover:border-emerald-500/40"
                        }`}
                      >
                        {isActive && playing && (
                          <span className="animate-pulse-ring absolute inset-0 rounded-full border-2 border-emerald-400" />
                        )}
                        {isActive && playing ? (
                          <Pause className="h-5 w-5 fill-current" />
                        ) : (
                          <Play className="h-5 w-5 fill-current pl-0.5" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-2 font-display text-base font-bold tracking-tight text-slate-100">
                          <Icon className="h-4 w-4 shrink-0 text-emerald-400" />
                          {s.tabs[i]}
                        </p>
                        <p className="mt-1 flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                          <BadgeCheck className="h-3.5 w-3.5 text-cyan-400" />
                          {s.note} · {isActive && d ? `${fmt(now)} / ${fmt(d)}` : c.dur}
                        </p>
                        {isActive && d && (
                          <div className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-slate-800">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400 transition-[width] duration-200"
                              style={{ width: `${Math.min(100, (now / d) * 100)}%` }}
                            />
                          </div>
                        )}
                      </div>
                      {isActive && playing && (
                        <div className="flex h-6 items-center gap-[3px]">
                          {[0, 1, 2, 3, 4].map((b) => (
                            <span
                              key={b}
                              className="eq-bar w-[3px] rounded-full bg-gradient-to-t from-cyan-400 to-emerald-400"
                              style={{ height: "100%", animationDelay: `${b * 0.12}s` }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-3">
            <div className="glass flex h-full flex-col overflow-hidden rounded-3xl">
              <div className="flex items-center justify-between border-b border-slate-800/70 px-6 py-4">
                <div className="flex items-center gap-3">
                  <LogoMark size={32} />
                  <div>
                    <p className="font-display text-sm font-bold text-slate-100">
                      {activeData ? activeData.aiName : "GWD AI"}
                    </p>
                    <p className="font-mono2 text-[10px] uppercase tracking-widest text-emerald-400">{s.live}</p>
                  </div>
                </div>
                <span className="font-mono2 text-[10px] tracking-widest text-slate-500">+41 ·· ··· ·· ··</span>
              </div>

              <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-6 py-6" style={{ minHeight: "24rem", maxHeight: "28rem" }}>
                {!activeData && (
                  <div className="flex h-full min-h-[20rem] items-center justify-center">
                    <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-slate-600">— GWD AI —</p>
                  </div>
                )}
                <AnimatePresence>
                  {activeData &&
                    activeData.lines.slice(0, visibleCount).map((l, idx) => (
                      <motion.div
                        key={`${active}-${idx}`}
                        initial={{ opacity: 0, y: 14, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className={`flex ${l.who === "ai" ? "justify-start" : "justify-end"}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                            l.who === "ai"
                              ? "rounded-bl-sm border border-emerald-500/25 bg-emerald-500/10 text-slate-100"
                              : "rounded-br-sm bg-slate-800/80 text-slate-300"
                          }`}
                        >
                          <p
                            className={`mb-1 font-mono2 text-[9px] uppercase tracking-[0.2em] ${
                              l.who === "ai" ? "text-emerald-400" : "text-slate-500"
                            }`}
                          >
                            {l.who === "ai" ? activeData.aiName : s.caller}
                          </p>
                          {l.text}
                          {lang !== "it" && l.tr && l.tr[lang] && (
                            <p className="mt-1.5 border-t border-slate-600/30 pt-1.5 text-xs italic leading-relaxed opacity-70">
                              {l.tr[lang]}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    ))}
                </AnimatePresence>
                {ended && activeData && (
                  <motion.div
                    initial={{ opacity: 0, y: 14, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className="flex justify-center pt-2"
                    data-testid="demo-confirmation"
                  >
                    <div className="inline-flex items-center gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-5 py-3 shadow-[0_0_30px_rgba(16,185,129,0.2)]">
                      <CalendarCheck2 className="h-5 w-5 text-emerald-400" />
                      <div>
                        <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-emerald-400">{s.confirm}</p>
                        <p className="mt-1.5 text-sm font-semibold text-slate-100">{(activeData.confirmTr && activeData.confirmTr[lang]) || activeData.confirm}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
